import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { mdyDow } from "@/lib/dateFmt";
import { Users2, X } from "lucide-react";

export default function CoverageDialog({ open, onClose, coverage, date, facilityId, onSaved }) {
  const [candidates, setCandidates] = useState([]);
  const [pickId, setPickId] = useState(null);
  const [saving, setSaving] = useState(false);

  // `coverage` may be:
  //   (a) an OPEN coverage slot from open-shifts strip: { assignment_id, code, source_employee_id, source_employee_name, gap_start, gap_end, gap_hours }
  //   (b) an ASSIGNED coverage_shift from an employee cell: { source_assignment_id, employee_id, employee_name, code, start_time, end_time, hours, source_employee_id, source_employee_name }
  const assignmentId = coverage?.assignment_id || coverage?.source_assignment_id;
  const existingCovererId = coverage?.employee_id;    // populated only in mode (b)
  const existingCovererName = coverage?.employee_name;
  const startT = coverage?.gap_start || coverage?.start_time;
  const endT = coverage?.gap_end || coverage?.end_time;
  const covHours = coverage?.gap_hours ?? coverage?.hours ?? 0;
  const isAssigned = !!existingCovererId;

  useEffect(() => {
    if (!open || !facilityId) return;
    setPickId(null);
    (async () => {
      try {
        const { data } = await api.get(`/employees?facility_id=${facilityId}`);
        setCandidates(data.filter(e => e.id !== coverage?.source_employee_id));
      } catch { setCandidates([]); }
    })();
  }, [open, facilityId, coverage?.source_employee_id]);

  const gapLabel = useMemo(() =>
    startT && endT ? `${startT} – ${endT} (${covHours}h)` : `${covHours || 0}h`
  , [startT, endT, covHours]);

  const save = async () => {
    if (!pickId || !assignmentId) return;
    setSaving(true);
    try {
      if (isAssigned) {
        // Reassign: clear existing first, then assign the new one
        await api.delete(`/schedule/coverage-assign/${assignmentId}`);
      }
      await api.post("/schedule/coverage-assign", {
        assignment_id: assignmentId,
        employee_id: pickId,
      });
      toast.success(isAssigned ? "Coverage reassigned" : "Coverage assigned");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to assign coverage");
    }
    setSaving(false);
  };

  const clear = async () => {
    if (!assignmentId) return;
    setSaving(true);
    try {
      await api.delete(`/schedule/coverage-assign/${assignmentId}`);
      toast.success("Coverage cleared — back on open shifts");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to clear coverage");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto thin-scroll" data-testid="coverage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Users2 size={18} className="text-pink-600" />
            {isAssigned ? "Manage coverage" : "Assign coverage"} {coverage ? `· ${coverage.code}` : ""}
          </DialogTitle>
          <DialogDescription>
            {date ? mdyDow(date) : ""}
            {coverage?.source_employee_name && (
              <> · <b>{coverage.source_employee_name}</b> can&apos;t work {gapLabel}</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-pink-200 bg-pink-50/50 px-3 py-2 text-sm text-pink-900 space-y-1">
          <div>Coverage window: <b>{gapLabel}</b></div>
          {isAssigned && (
            <div className="text-xs">Currently covered by <b>{existingCovererName}</b></div>
          )}
        </div>

        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-widest text-slate-500">
            {isAssigned ? "Reassign to (optional)" : "Pick a coverer"}
          </Label>
          <div className="max-h-72 overflow-y-auto thin-scroll rounded-lg border border-slate-200 divide-y">
            {candidates.length === 0 && (
              <div className="p-4 text-sm text-slate-500 text-center">No available employees on this facility.</div>
            )}
            {candidates.map((c) => {
              const active = pickId === c.id;
              const isCurrent = c.id === existingCovererId;
              return (
                <button
                  key={c.id}
                  data-testid={`cov-pick-${c.id}`}
                  onClick={() => setPickId(c.id)}
                  className={`w-full flex items-center gap-3 p-2 text-left transition-colors ${active ? "bg-pink-50" : "hover:bg-slate-50"}`}
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={c.avatar} />
                    <AvatarFallback>{c.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{c.role_label || c.position || c.role}</div>
                  </div>
                  {isCurrent && <span className="text-[10px] uppercase tracking-widest text-pink-700 font-semibold">CURRENT</span>}
                  {active && !isCurrent && <span className="text-pink-600 text-xs font-semibold">SELECTED</span>}
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter className="sm:justify-between gap-2 pt-2">
          <div>
            {isAssigned && (
              <Button
                variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={clear} disabled={saving}
                data-testid="coverage-clear"
              >
                <X size={14} className="mr-1" /> Clear coverage
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="coverage-cancel">Cancel</Button>
            <Button
              className="bg-pink-600 hover:bg-pink-700 text-white"
              onClick={save} disabled={!pickId || saving || pickId === existingCovererId}
              data-testid="coverage-save"
            >
              {isAssigned ? "Reassign" : "Assign coverage"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
